import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Users, UserPlus, Shield, ShieldCheck, Mail, Trash2, Edit2,
  Crown, User, Check, X, AlertTriangle
} from 'lucide-react'
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, setDoc, where, getDocs } from 'firebase/firestore'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { db, auth } from '../../services/firebase'
import { formatDate, formatRelativeTime } from '../../utils/helpers'
import { Button, Modal, Input, Select, Card, Badge } from '../../components/common'
import { useAuth } from '../../context/AuthContext'

const roles = [
  { value: 'super_admin', label: 'Super Admin', icon: Crown, color: 'text-amber-600', bg: 'bg-amber-100' },
  { value: 'admin', label: 'Admin', icon: ShieldCheck, color: 'text-primary-600', bg: 'bg-primary-100' },
  { value: 'editor', label: 'Editor', icon: Shield, color: 'text-secondary-600', bg: 'bg-secondary-100' },
]

const UserForm = ({ user, onSave, onClose, currentUserRole }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    displayName: user?.displayName || '',
    role: user?.role || 'admin',
    password: '',
    confirmPassword: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const isEditing = !!user

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!isEditing) {
      if (!formData.password) {
        setError('Password is required for new users')
        return
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters')
        return
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match')
        return
      }
    }

    // Only super_admin can create other super_admins
    if (formData.role === 'super_admin' && currentUserRole !== 'super_admin') {
      setError('Only Super Admins can create other Super Admins')
      return
    }

    setSaving(true)
    try {
      await onSave({
        email: formData.email,
        displayName: formData.displayName,
        role: formData.role,
        password: isEditing ? null : formData.password,
      })
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}

      <Input
        label="Full Name"
        required
        value={formData.displayName}
        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
        placeholder="John Smith"
      />

      <Input
        label="Email Address"
        type="email"
        required
        disabled={isEditing}
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        placeholder="john@example.com"
      />

      <Select
        label="Role"
        required
        options={roles.filter(r => currentUserRole === 'super_admin' || r.value !== 'super_admin')}
        value={formData.role}
        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
      />

      {!isEditing && (
        <>
          <Input
            label="Password"
            type="password"
            required
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="Minimum 6 characters"
          />

          <Input
            label="Confirm Password"
            type="password"
            required
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            placeholder="Repeat password"
          />
        </>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-dark-100 mb-2">Role Permissions</h4>
        <div className="space-y-2 text-sm text-gray-600">
          {formData.role === 'super_admin' && (
            <>
              <p>Full access to all features</p>
              <p>Can manage other users and admins</p>
              <p>Can delete any content</p>
            </>
          )}
          {formData.role === 'admin' && (
            <>
              <p>Full access to content management</p>
              <p>Can manage events, sermons, ministries</p>
              <p>Cannot manage other users</p>
            </>
          )}
          {formData.role === 'editor' && (
            <>
              <p>Can create and edit content</p>
              <p>Cannot delete content</p>
              <p>Limited access to settings</p>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {isEditing ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  )
}

const UsersManager = () => {
  const { user: currentUser, userRole: currentUserRole } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    if (!db) {
      setLoading(false)
      return
    }

    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setUsers(data)
      setLoading(false)
    }, (error) => {
      console.error('Error fetching users:', error)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const handleSave = async (data) => {
    if (!db) return

    try {
      if (editingUser) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingUser.id), {
          displayName: data.displayName,
          role: data.role,
          updatedAt: new Date().toISOString(),
        })
      } else {
        // Create new user with Firebase Auth
        if (!auth) throw new Error('Authentication not configured')

        const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password)

        // Add user to Firestore
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          uid: userCredential.user.uid,
          email: data.email,
          displayName: data.displayName,
          role: data.role,
          isActive: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.uid,
        })
      }

      setShowModal(false)
      setEditingUser(null)
    } catch (error) {
      console.error('Error saving user:', error)
      throw error
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm || !db) return

    try {
      // Can't delete yourself
      if (deleteConfirm.uid === currentUser?.uid) {
        alert("You cannot delete your own account")
        setDeleteConfirm(null)
        return
      }

      // Can't delete super_admin unless you're super_admin
      if (deleteConfirm.role === 'super_admin' && currentUserRole !== 'super_admin') {
        alert("Only Super Admins can delete other Super Admins")
        setDeleteConfirm(null)
        return
      }

      await deleteDoc(doc(db, 'users', deleteConfirm.id))
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Error deleting user:', error)
    }
  }

  const toggleUserStatus = async (user) => {
    if (!db) return

    // Can't deactivate yourself
    if (user.uid === currentUser?.uid) {
      alert("You cannot deactivate your own account")
      return
    }

    await updateDoc(doc(db, 'users', user.id), {
      isActive: !user.isActive,
      updatedAt: new Date().toISOString(),
    })
  }

  const getRoleInfo = (role) => {
    return roles.find(r => r.value === role) || roles[1]
  }

  const canManageUsers = currentUserRole === 'super_admin' || currentUserRole === 'admin'

  if (!canManageUsers) {
    return (
      <div className="text-center py-12">
        <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-xl font-semibold text-dark-100 mb-2">Access Restricted</h2>
        <p className="text-gray-500">You don't have permission to manage users.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">User Management</h1>
          <p className="text-gray-500">Manage admin users and their permissions</p>
        </div>
        <Button
          variant="primary"
          icon={UserPlus}
          onClick={() => {
            setEditingUser(null)
            setShowModal(true)
          }}
        >
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card animate={false} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {users.filter(u => u.role === 'super_admin').length}
              </p>
              <p className="text-sm text-gray-500">Super Admins</p>
            </div>
          </div>
        </Card>
        <Card animate={false} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {users.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-sm text-gray-500">Admins</p>
            </div>
          </div>
        </Card>
        <Card animate={false} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {users.filter(u => u.role === 'editor').length}
              </p>
              <p className="text-sm text-gray-500">Editors</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Users Table */}
      <Card animate={false} className="overflow-hidden p-0">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">User</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => {
                  const roleInfo = getRoleInfo(user.role)
                  const RoleIcon = roleInfo.icon
                  const isCurrentUser = user.uid === currentUser?.uid

                  return (
                    <tr key={user.id} className={`hover:bg-gray-50 ${isCurrentUser ? 'bg-primary-50/30' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center text-white font-semibold">
                            {user.displayName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-dark-100 flex items-center gap-2">
                              {user.displayName || 'No Name'}
                              {isCurrentUser && (
                                <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">You</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${roleInfo.bg} ${roleInfo.color}`}>
                          <RoleIcon className="w-4 h-4" />
                          {roleInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.isActive !== false ? (
                          <Badge variant="emerald" size="sm">Active</Badge>
                        ) : (
                          <Badge variant="gray" size="sm">Inactive</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm">
                        {formatRelativeTime(user.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => toggleUserStatus(user)}
                            disabled={isCurrentUser}
                            className={`p-2 rounded-lg transition-colors ${
                              isCurrentUser
                                ? 'text-gray-300 cursor-not-allowed'
                                : user.isActive !== false
                                  ? 'text-emerald-600 hover:bg-emerald-50'
                                  : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={user.isActive !== false ? 'Deactivate' : 'Activate'}
                          >
                            {user.isActive !== false ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => {
                              setEditingUser(user)
                              setShowModal(true)
                            }}
                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(user)}
                            disabled={isCurrentUser}
                            className={`p-2 rounded-lg transition-colors ${
                              isCurrentUser
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No users yet</p>
            <p className="text-sm mt-1">Add your first admin user to get started</p>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingUser(null)
        }}
        title={editingUser ? 'Edit User' : 'Add New User'}
        size="lg"
      >
        <UserForm
          user={editingUser}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingUser(null)
          }}
          currentUserRole={currentUserRole}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete User"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <p className="font-medium text-red-800">This action cannot be undone</p>
              <p className="text-sm text-red-600">The user will lose access to the admin dashboard.</p>
            </div>
          </div>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{deleteConfirm?.displayName || deleteConfirm?.email}</strong>?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete User
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default UsersManager
